import { queryOptions } from '@tanstack/react-query';
import type { NotificationPrefs, Profile, TwoFactorStatus, UserSession } from '@erp/shared';
import { api } from '@/lib/api-client';

export function profileQueryOptions() {
  return queryOptions({
    queryKey: ['erp', 'account', 'profile'] as const,
    queryFn: async () => {
      const res = await api<Profile>('/me');
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function sessionsQueryOptions() {
  return queryOptions({
    queryKey: ['erp', 'account', 'sessions'] as const,
    queryFn: async () => {
      const res = await api<UserSession[]>('/me/sessions');
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

export function notificationPrefsQueryOptions() {
  return queryOptions({
    queryKey: ['erp', 'account', 'notification-prefs'] as const,
    queryFn: async () => {
      const res = await api<NotificationPrefs>('/me/notification-prefs');
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 60_000,
  });
}

// ── Formatação ──────────────────────────────────────────────────────────────

/** "dd/mm/aaaa hh:mm" em pt-BR. */
export function formatDateTime(value: string | Date): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('pt-BR');
  const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${date} ${time}`;
}

/** Parse simples de userAgent → "<Browser> no <OS>" (fallback "Dispositivo"). */
export function describeUserAgent(ua: string | null): string {
  if (!ua) return 'Dispositivo';

  let browser: string | null = null;
  if (/edg(a|ios)?\//i.test(ua)) browser = 'Edge';
  else if (/firefox|fxios/i.test(ua)) browser = 'Firefox';
  else if (/chrome|crios/i.test(ua)) browser = 'Chrome';
  else if (/safari/i.test(ua)) browser = 'Safari';

  let os: string | null = null;
  if (/iphone|ipad|ipod/i.test(ua)) os = 'iOS';
  else if (/android/i.test(ua)) os = 'Android';
  else if (/mac os x|macintosh/i.test(ua)) os = 'macOS';
  else if (/windows/i.test(ua)) os = 'Windows';
  else if (/linux/i.test(ua)) os = 'Linux';

  if (browser && os) return `${browser} no ${os}`;
  if (browser) return browser;
  if (os) return `Dispositivo no ${os}`;
  return 'Dispositivo';
}

export function twoFactorQuery() {
  return queryOptions({
    queryKey: ['erp', 'account', '2fa'] as const,
    queryFn: async ({ signal }) => {
      const res = await api<TwoFactorStatus>('/me/2fa', { signal });
      if (res.error) throw res.error;
      return res.data;
    },
    staleTime: 30_000,
  });
}
